import { Fragment, useState, useEffect, useCallback, FormEvent } from "react";
import { useAdminAuth } from "@/lib/adminAuth";

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Agent {
  id: number;
  name: string;
  role: string;
  description: string;
  price: number;
  active: boolean;
  sortOrder: number;
  imageUrl?: string | null;
}

const EMPTY: Omit<Agent, "id"> = {
  name: "",
  role: "",
  description: "",
  price: 0,
  active: true,
  sortOrder: 0,
  imageUrl: null,
};

export default function AgentsTab() {
  const { adminFetch } = useAdminAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<Agent | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Omit<Agent, "id">>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [reordering, setReordering] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [uploadingId, setUploadingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch("api/admin/agents");
      if (!res.ok) throw new Error("Failed to load");
      setAgents(await res.json());
      setError("");
    } catch (e: unknown) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { load(); }, [load]);

  const move = async (idx: number, direction: "up" | "down") => {
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= agents.length) return;

    const next = [...agents];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setAgents(next);
    setReordering(next[swapIdx].id);

    try {
      const res = await adminFetch("api/admin/agents/reorder", {
        method: "PUT",
        body: JSON.stringify({ order: next.map((a) => a.id) }),
      });
      if (!res.ok) throw new Error("Reorder failed");
    } catch (e: unknown) {
      alert(errMsg(e));
      await load();
    } finally {
      setReordering(null);
    }
  };

  const openEdit = (a: Agent) => {
    setEditing(a);
    setForm({ name: a.name, role: a.role, description: a.description, price: a.price, active: a.active, sortOrder: a.sortOrder, imageUrl: a.imageUrl ?? null });
    setCreating(false);
  };

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm(EMPTY);
  };

  const closeModal = () => { setEditing(null); setCreating(false); };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, price: Number(form.price), sortOrder: Number(form.sortOrder) };
      let res;
      if (creating) {
        res = await adminFetch("api/admin/agents", { method: "POST", body: JSON.stringify(body) });
      } else if (editing) {
        res = await adminFetch(`api/admin/agents/${editing.id}`, { method: "PUT", body: JSON.stringify(body) });
      }
      if (!res?.ok) throw new Error("Save failed");
      closeModal();
      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await adminFetch(`api/admin/agents/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      setDeleteId(null);
      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    }
  };

  const toggleActive = async (a: Agent) => {
    try {
      const res = await adminFetch(`api/admin/agents/${a.id}`, {
        method: "PUT",
        body: JSON.stringify({ active: !a.active }),
      });
      if (!res.ok) throw new Error("Toggle failed");
      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const handleImageUpload = async (agentId: number, file: File) => {
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      alert("Please select a JPEG, PNG, WebP, or GIF image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be 5 MB or smaller.");
      return;
    }

    setUploadingId(agentId);
    try {
      const urlRes = await adminFetch(`api/admin/agents/${agentId}/image`, {
        method: "POST",
        body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
      });
      if (!urlRes.ok) {
        const err = await urlRes.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to get upload URL");
      }
      const { uploadURL, objectPath } = await urlRes.json();

      const uploadRes = await fetch(uploadURL, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadRes.ok) throw new Error("Upload to storage failed");

      const saveRes = await adminFetch(`api/admin/agents/${agentId}`, {
        method: "PUT",
        body: JSON.stringify({ imageUrl: objectPath }),
      });
      if (!saveRes.ok) throw new Error("Failed to save image URL");

      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    } finally {
      setUploadingId(null);
    }
  };

  const handleRemoveImage = async (agentId: number) => {
    try {
      const res = await adminFetch(`api/admin/agents/${agentId}`, {
        method: "PUT",
        body: JSON.stringify({ imageUrl: null }),
      });
      if (!res.ok) throw new Error("Failed to remove image");
      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    }
  };

  if (loading) return <div className="text-white/50 py-8 text-center">Loading agents…</div>;
  if (error) return <div className="text-red-400 py-8 text-center">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Agents ({agents.length})</h2>
        <button
          onClick={openCreate}
          className="bg-purple-600 hover:bg-purple-500 text-white text-sm px-4 py-2 rounded-lg transition"
        >
          + Add Agent
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-white/50 text-left">
              <th className="px-3 py-3 font-medium w-16 text-center">Order</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Price</th>
              <th className="px-4 py-3 font-medium">Active</th>
              <th className="px-4 py-3 font-medium">Actions</th>
              <th className="px-3 py-3 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a, idx) => {
              const isExpanded = expandedId === a.id;
              const imgSrc = a.imageUrl ? `${BASE}/api/storage${a.imageUrl}` : null;
              return (
                <Fragment key={a.id}>
                  <tr
                    onClick={() => toggleExpand(a.id)}
                    className={`border-b border-white/5 cursor-pointer transition ${reordering === a.id ? "opacity-60" : "hover:bg-white/5"} ${isExpanded ? "bg-white/5" : ""}`}
                  >
                    <td className="px-3 py-3">
                      <div className="flex flex-col items-center gap-0.5">
                        <button
                          onClick={e => { e.stopPropagation(); move(idx, "up"); }}
                          disabled={idx === 0 || reordering !== null}
                          className="text-white/30 hover:text-white/80 disabled:opacity-20 disabled:cursor-not-allowed transition p-0.5 rounded"
                          title="Move up"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <span className="text-white/25 text-[10px] leading-none font-mono">{idx + 1}</span>
                        <button
                          onClick={e => { e.stopPropagation(); move(idx, "down"); }}
                          disabled={idx === agents.length - 1 || reordering !== null}
                          className="text-white/30 hover:text-white/80 disabled:opacity-20 disabled:cursor-not-allowed transition p-0.5 rounded"
                          title="Move down"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {imgSrc ? (
                          <img src={imgSrc} alt={a.name} className="w-7 h-7 rounded-md object-cover border border-white/10 shrink-0" />
                        ) : (
                          <div className="w-7 h-7 rounded-md bg-white/5 border border-white/10 shrink-0 flex items-center justify-center">
                            <svg className="w-3.5 h-3.5 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                        <span className="text-white font-medium">{a.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white">${a.price.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={e => { e.stopPropagation(); toggleActive(a); }}
                        className={`w-10 h-5 rounded-full transition-colors ${a.active ? "bg-purple-600" : "bg-white/20"} relative`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${a.active ? "left-5" : "left-0.5"}`} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={e => { e.stopPropagation(); openEdit(a); }}
                          className="text-purple-400 hover:text-purple-300 transition text-xs border border-purple-400/30 hover:border-purple-300/50 rounded px-2 py-1"
                        >
                          Edit
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); setDeleteId(a.id); }}
                          className="text-red-400 hover:text-red-300 transition text-xs border border-red-400/30 hover:border-red-300/50 rounded px-2 py-1"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-white/30">
                      <svg
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-b border-white/5 bg-white/[0.03]">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="flex flex-col gap-4">
                          <div>
                            <span className="text-[11px] font-medium text-white/40 uppercase tracking-wider mb-2 block">Image</span>
                            <div className="flex items-center gap-3">
                              {imgSrc ? (
                                <img src={imgSrc} alt={a.name} className="w-14 h-14 rounded-xl object-cover border border-white/10 shrink-0" />
                              ) : (
                                <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 shrink-0 flex items-center justify-center">
                                  <svg className="w-6 h-6 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <label className={`cursor-pointer text-xs border rounded px-2.5 py-1.5 transition leading-tight ${uploadingId === a.id ? "text-white/30 border-white/10 cursor-not-allowed" : "text-purple-400 hover:text-purple-300 border-purple-400/30 hover:border-purple-300/50"}`}>
                                  {uploadingId === a.id ? "Uploading…" : a.imageUrl ? "Change Image" : "Upload Image"}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    disabled={uploadingId !== null}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(a.id, file);
                                      e.target.value = "";
                                    }}
                                  />
                                </label>
                                {a.imageUrl && uploadingId !== a.id && (
                                  <button
                                    onClick={() => handleRemoveImage(a.id)}
                                    className="text-red-400 hover:text-red-300 transition text-xs border border-red-400/30 hover:border-red-300/50 rounded px-2.5 py-1.5"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          <div>
                            <span className="text-[11px] font-medium text-white/40 uppercase tracking-wider">Role</span>
                            <p className="mt-1 text-sm text-white/80">{a.role}</p>
                          </div>
                          <div>
                            <span className="text-[11px] font-medium text-white/40 uppercase tracking-wider">Description</span>
                            <p className="mt-1 text-sm text-white/70 leading-relaxed">{a.description}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {(editing || creating) && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#131320] border border-white/10 rounded-2xl p-6 w-full max-w-lg">
            <h3 className="text-white font-semibold text-lg mb-4">{creating ? "Add Agent" : "Edit Agent"}</h3>
            <form onSubmit={handleSave} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/50 mb-1">Name</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">Price ($)</label>
                  <input
                    type="number"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                    required min={0}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Role</label>
                <input
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 resize-none"
                  rows={3}
                  required
                />
              </div>
              <div className="flex items-center gap-3 pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="accent-purple-500 w-4 h-4"
                  />
                  <span className="text-sm text-white/70">Active</span>
                </label>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 border border-white/20 text-white/70 py-2 rounded-lg hover:bg-white/5 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-purple-600 hover:bg-purple-500 disabled:opacity-60 text-white py-2 rounded-lg transition text-sm font-medium"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#131320] border border-white/10 rounded-2xl p-6 w-full max-w-sm text-center">
            <p className="text-white font-medium mb-2">Delete this agent?</p>
            <p className="text-white/50 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 border border-white/20 text-white/70 py-2 rounded-lg hover:bg-white/5 transition text-sm">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2 rounded-lg transition text-sm font-medium">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
